import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  BarChart3,
  Users,
  Eye,
  MousePointerClick,
  Smartphone,
  Monitor,
  Tablet,
  RefreshCw,
  Radio,
  Link as LinkIcon,
  Globe,
  Clock,
  TrendingUp,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { authFetch } from '../utils/adminAuth';
import AdminShell, { StatCard } from '../components/admin/AdminShell';

const RANGE_OPTIONS = [
  { label: '24h', days: 1 },
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

const relativeTime = (iso) => {
  if (!iso) return '—';
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch {
    return '—';
  }
};

const DeviceIcon = ({ device }) => {
  const cls = 'w-4 h-4';
  if (device === 'mobile') return <Smartphone className={cls} strokeWidth={2.25} />;
  if (device === 'tablet') return <Tablet className={cls} strokeWidth={2.25} />;
  if (device === 'desktop') return <Monitor className={cls} strokeWidth={2.25} />;
  return <Globe className={cls} strokeWidth={2.25} />;
};

// -------- Sparkline / bar chart -------- //
const DailyBarChart = ({ series }) => {
  if (!series || series.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-slate-500 text-sm">
        No data in this window yet
      </div>
    );
  }
  const max = Math.max(1, ...series.map((d) => d.pageviews));
  return (
    <div className="pt-4">
      <div className="h-48 flex items-end gap-1.5 px-2">
        {series.map((d) => {
          const heightPct = (d.pageviews / max) * 100;
          return (
            <div
              key={d.date}
              className="group relative flex-1 h-full flex items-end justify-center min-w-0"
              title={`${d.date} · ${d.pageviews} views · ${d.visitors} visitors`}
            >
              <div
                className="w-full bg-gradient-to-t from-blue-500 via-indigo-500 to-purple-500 rounded-t-md min-h-[3px] transition-all group-hover:from-cyan-400 group-hover:via-blue-400 group-hover:to-purple-400 group-hover:shadow-lg group-hover:shadow-blue-500/30"
                style={{ height: `${heightPct}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-1.5 px-2 mt-2">
        {series.map((d) => (
          <span
            key={d.date}
            className="flex-1 text-center text-[10px] text-slate-500 tabular-nums truncate"
          >
            {d.date.slice(5)}
          </span>
        ))}
      </div>
    </div>
  );
};

// -------- Horizontal bar row -------- //
const BarRow = ({ label, count, max, icon: Icon, mono, testId }) => {
  const pct = max ? Math.max(4, (count / max) * 100) : 0;
  return (
    <div className="group py-2" data-testid={testId}>
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {Icon && <Icon className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" strokeWidth={2.25} />}
          <span className={`text-[13px] text-slate-200 truncate ${mono ? 'font-mono' : ''}`}>
            {label}
          </span>
        </div>
        <span className="text-[12px] tabular-nums text-slate-400 font-semibold flex-shrink-0">
          {count.toLocaleString()}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-800/70 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-purple-500 group-hover:from-cyan-400 group-hover:to-purple-400 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const AdminAnalyticsPage = () => {
  const [rangeDays, setRangeDays] = useState(7);
  const [overview, setOverview] = useState(null);
  const [live, setLive] = useState(null);
  const [leadSources, setLeadSources] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingLive, setLoadingLive] = useState(true);
  const [loadingSources, setLoadingSources] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState(null);
  const liveTimerRef = useRef(null);

  // Convert an ISO-2 country code into a friendly name + flag emoji.
  const countryDisplay = useCallback((code) => {
    const cc = (code || '').toUpperCase().trim();
    if (!cc || cc.length !== 2 || !/^[A-Z]{2}$/.test(cc)) {
      return { flag: '🌐', name: cc || '—' };
    }
    // Regional-indicator symbols: A=0x1F1E6, so code-point offset works.
    const flag = String.fromCodePoint(
      ...cc.split('').map((c) => 127397 + c.charCodeAt(0)),
    );
    let name = cc;
    try {
      const dn = new Intl.DisplayNames(['en'], { type: 'region' });
      name = dn.of(cc) || cc;
    } catch {
      /* older browser — leave the code */
    }
    return { flag, name };
  }, []);

  const fetchOverview = useCallback(async (days) => {
    setLoadingOverview(true);
    try {
      const resp = await authFetch(`/api/admin/analytics/overview?days=${days}`);
      if (!resp.ok) return;
      const data = await resp.json();
      setOverview(data);
    } catch (e) {
      console.error('overview fetch failed', e);
    } finally {
      setLoadingOverview(false);
    }
  }, []);

  const fetchLeadSources = useCallback(async (days) => {
    setLoadingSources(true);
    try {
      // Lead-source aggregation is always over a wider window (30d default)
      // so the chart is populated even when the visitor toggle is on 24h.
      const window = Math.max(days, 30);
      const resp = await authFetch(`/api/admin/analytics/lead-sources?days=${window}`);
      if (!resp.ok) return;
      const data = await resp.json();
      setLeadSources(data);
    } catch (e) {
      console.error('lead-sources fetch failed', e);
    } finally {
      setLoadingSources(false);
    }
  }, []);

  const fetchLive = useCallback(async () => {
    try {
      const resp = await authFetch('/api/admin/analytics/live');
      if (!resp.ok) return;
      const data = await resp.json();
      setLive(data);
    } catch (e) {
      console.error('live fetch failed', e);
    } finally {
      setLoadingLive(false);
    }
  }, []);

  const handleBackfillGeo = useCallback(async () => {
    setBackfilling(true);
    setBackfillMsg(null);
    try {
      const resp = await authFetch('/api/admin/analytics/backfill-geo?limit=500', {
        method: 'POST',
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        setBackfillMsg(
          `Enriched ${data.updated || 0} IPs · skipped ${data.skipped || 0}. Refreshing…`,
        );
        // Re-fetch overview so new country column shows up
        setTimeout(() => {
          fetchOverview(rangeDays);
          setBackfillMsg(null);
        }, 900);
      } else {
        setBackfillMsg(data.detail || 'Backfill failed');
      }
    } catch (e) {
      setBackfillMsg('Network error during backfill');
    } finally {
      setBackfilling(false);
    }
  }, [fetchOverview, rangeDays]);

  useEffect(() => {
    fetchOverview(rangeDays);
    fetchLeadSources(rangeDays);
  }, [rangeDays, fetchOverview, fetchLeadSources]);

  useEffect(() => {
    fetchLive();
    // Poll every 15s so "who's online now" stays fresh without websockets
    liveTimerRef.current = setInterval(fetchLive, 15000);
    return () => clearInterval(liveTimerRef.current);
  }, [fetchLive]);

  const totals = overview?.totals || { pageviews: 0, unique_visitors: 0, sessions: 0, views_per_visitor: 0 };
  const deviceTotal = useMemo(() => {
    const d = overview?.devices || {};
    return (d.mobile || 0) + (d.tablet || 0) + (d.desktop || 0) + (d.unknown || 0);
  }, [overview]);

  const topPagesMax = useMemo(() => {
    const arr = overview?.top_pages || [];
    return arr.length ? Math.max(...arr.map((p) => p.views)) : 0;
  }, [overview]);

  const topRefMax = useMemo(() => {
    const arr = overview?.top_referrers || [];
    return arr.length ? Math.max(...arr.map((p) => p.views)) : 0;
  }, [overview]);

  const activePagesMax = useMemo(() => {
    const arr = live?.active_pages || [];
    return arr.length ? Math.max(...arr.map((p) => p.visitors)) : 0;
  }, [live]);

  return (
    <AdminShell
      pageTitle="Analytics"
      pageDescription="Live visitors, traffic sources, and site engagement — first-party, no cookies."
      rightSlot={
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-1 p-1 rounded-md bg-slate-900/70 border border-slate-800">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.days}
                onClick={() => setRangeDays(opt.days)}
                data-testid={`analytics-range-${opt.label}`}
                className={`px-2.5 h-7 rounded text-[12px] font-semibold tracking-wide transition-all ${
                  rangeDays === opt.days
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => {
              fetchOverview(rangeDays);
              fetchLive();
            }}
            data-testid="analytics-refresh-btn"
            className="flex items-center gap-1.5 px-3 h-9 rounded-md text-[13px] font-medium text-slate-300 hover:text-white hover:bg-slate-800/70 border border-slate-800 hover:border-slate-700 transition-all"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${loadingOverview || loadingLive ? 'animate-spin' : ''}`}
              strokeWidth={2.25}
            />
            Refresh
          </button>
        </div>
      }
    >
      {/* Mobile range switcher */}
      <div className="sm:hidden mb-4 flex items-center gap-1 p-1 rounded-md bg-slate-900/70 border border-slate-800 w-fit">
        {RANGE_OPTIONS.map((opt) => (
          <button
            key={opt.days}
            onClick={() => setRangeDays(opt.days)}
            className={`px-2.5 h-7 rounded text-[12px] font-semibold ${
              rangeDays === opt.days
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                : 'text-slate-400'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Live strip */}
      <div
        className="mb-6 rounded-xl border border-emerald-500/25 bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent p-4"
        data-testid="analytics-live-strip"
      >
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="relative w-11 h-11 rounded-lg bg-emerald-500/15 flex items-center justify-center ring-1 ring-emerald-500/30">
              <Radio className="w-5 h-5 text-emerald-300" strokeWidth={2.25} />
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-slate-950 animate-pulse" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/80 font-bold">
                Live · last 5 min
              </div>
              <div className="text-white text-3xl font-display font-bold tabular-nums leading-none mt-1">
                {loadingLive ? '—' : (live?.live_visitors ?? 0).toLocaleString()}
              </div>
              <div className="text-slate-400 text-xs mt-1">
                {loadingLive
                  ? 'Connecting…'
                  : `${live?.live_visitors === 1 ? 'visitor' : 'visitors'} on the site right now`}
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1.5">
              Active pages
            </div>
            {live?.active_pages?.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {live.active_pages.slice(0, 6).map((p) => (
                  <div
                    key={p.path}
                    className="flex items-center justify-between px-2 py-1.5 rounded-md bg-slate-950/60 border border-slate-800/60 hover:border-emerald-500/30 transition-all"
                    title={`Last hit ${relativeTime(p.last_ts)}`}
                  >
                    <span className="text-[12px] text-slate-200 font-mono truncate flex-1">
                      {p.path}
                    </span>
                    <span className="text-[11px] font-bold text-emerald-300 tabular-nums pl-2">
                      {p.visitors}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-slate-500 text-xs italic">No live activity yet — check back shortly.</div>
            )}
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard
          label="Pageviews"
          value={totals.pageviews.toLocaleString()}
          icon={Eye}
          tone="info"
          tip={`Total pageviews in the last ${rangeDays}d`}
          testId="analytics-kpi-pageviews"
        />
        <StatCard
          label="Unique Visitors"
          value={totals.unique_visitors.toLocaleString()}
          icon={Users}
          tone="success"
          tip={`Distinct visitors in the last ${rangeDays}d`}
          testId="analytics-kpi-visitors"
        />
        <StatCard
          label="Sessions"
          value={totals.sessions.toLocaleString()}
          icon={MousePointerClick}
          tone="violet"
          tip={`Browser sessions in the last ${rangeDays}d`}
          testId="analytics-kpi-sessions"
        />
        <StatCard
          label="Views / Visitor"
          value={totals.views_per_visitor}
          icon={TrendingUp}
          tone="warning"
          tip="Average pageviews per unique visitor"
          testId="analytics-kpi-vpv"
        />
      </div>

      {/* Daily chart */}
      <div className="mb-6 rounded-xl border border-slate-800/70 bg-slate-900/50 p-5">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-white font-semibold text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-300" strokeWidth={2.25} />
              Daily traffic
            </h3>
            <p className="text-slate-500 text-[11px] mt-0.5">
              Pageviews per day for the last {rangeDays}d
            </p>
          </div>
        </div>
        <DailyBarChart series={overview?.daily_series} />
      </div>

      {/* Top Lead Sources — publisher signup attribution */}
      <div className="mb-6 rounded-xl border border-slate-800/70 bg-gradient-to-br from-indigo-500/5 via-slate-900/50 to-purple-500/5 p-5" data-testid="analytics-lead-sources">
        <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
          <div>
            <h3 className="text-white font-semibold text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-indigo-300" strokeWidth={2.25} />
              Top Lead Sources
              <span className="ml-1 px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 text-[9.5px] font-bold uppercase tracking-wider">
                Publisher Signups
              </span>
            </h3>
            <p className="text-slate-500 text-[11px] mt-0.5">
              Attribution for publisher applications · last 30d
              {leadSources ? ` · ${leadSources.total_signups} total signups` : ''}
            </p>
          </div>
        </div>

        {loadingSources ? (
          <div className="h-32 flex items-center justify-center text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" strokeWidth={2.5} />
            Loading attribution…
          </div>
        ) : leadSources?.sources?.length ? (
          <div className="mt-3 space-y-2">
            {(() => {
              const max = Math.max(...leadSources.sources.map((s) => s.count));
              return leadSources.sources.map((s) => {
                const pct = max ? Math.max(4, (s.count / max) * 100) : 0;
                const isDirect = s.source === '(direct)';
                const isTag = /^[a-z0-9_.:-]+$/i.test(s.source) && !s.source.includes('.com') && !s.source.includes('.co') && !isDirect;
                return (
                  <div key={s.source} className="group py-1" data-testid={`lead-source-${s.source}`}>
                    <div className="flex items-center justify-between gap-3 mb-1.5">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {isTag && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-indigo-500/20 text-indigo-200 border border-indigo-500/40 flex-shrink-0">
                            Tag
                          </span>
                        )}
                        <span
                          className={`text-[13px] truncate ${
                            isDirect ? 'text-slate-500 italic' : 'text-slate-100 font-mono'
                          }`}
                        >
                          {s.source}
                        </span>
                      </div>
                      <span className="text-[13px] tabular-nums text-white font-bold flex-shrink-0">
                        {s.count.toLocaleString()}
                        <span className="text-slate-500 font-normal ml-1.5 text-[11px]">
                          {leadSources.total_signups
                            ? `${Math.round((s.count / leadSources.total_signups) * 100)}%`
                            : ''}
                        </span>
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-800/70 overflow-hidden">
                      <div
                        className={`h-full transition-all group-hover:shadow-lg ${
                          isTag
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-500 group-hover:shadow-indigo-500/40'
                            : 'bg-gradient-to-r from-blue-500 to-cyan-500 group-hover:shadow-blue-500/40'
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        ) : (
          <div className="mt-3 py-8 text-center text-slate-500 text-sm italic">
            No publisher signups in this window.
            <div className="text-[11px] mt-2 text-slate-600">
              Tip: share <span className="font-mono text-blue-400">/apply?source=whatsapp_publisher</span> to
              start attributing leads by channel.
            </div>
          </div>
        )}
      </div>

      {/* Two-column grid: pages + referrers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="rounded-xl border border-slate-800/70 bg-slate-900/50 p-5" data-testid="analytics-top-pages">
          <h3 className="text-white font-semibold text-sm mb-1 flex items-center gap-2">
            <Activity className="w-4 h-4 text-cyan-300" strokeWidth={2.25} />
            Top pages
          </h3>
          <p className="text-slate-500 text-[11px] mb-4">Most-viewed public pages</p>
          {overview?.top_pages?.length ? (
            <div className="space-y-1">
              {overview.top_pages.map((p) => (
                <BarRow
                  key={p.path}
                  label={p.path}
                  count={p.views}
                  max={topPagesMax}
                  icon={LinkIcon}
                  mono
                />
              ))}
            </div>
          ) : (
            <div className="text-slate-500 text-sm italic py-6 text-center">No pageviews yet</div>
          )}
        </div>

        <div className="rounded-xl border border-slate-800/70 bg-slate-900/50 p-5" data-testid="analytics-top-referrers">
          <h3 className="text-white font-semibold text-sm mb-1 flex items-center gap-2">
            <Globe className="w-4 h-4 text-purple-300" strokeWidth={2.25} />
            Top referrers
          </h3>
          <p className="text-slate-500 text-[11px] mb-4">Where visitors are coming from</p>
          {overview?.top_referrers?.length ? (
            <div className="space-y-1">
              {overview.top_referrers.map((r) => (
                <BarRow
                  key={r.referrer}
                  label={r.referrer || '(direct)'}
                  count={r.views}
                  max={topRefMax}
                  mono
                />
              ))}
            </div>
          ) : (
            <div className="text-slate-500 text-sm italic py-6 text-center">No referrer data yet</div>
          )}
        </div>
      </div>

      {/* Devices + Countries + Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="rounded-xl border border-slate-800/70 bg-slate-900/50 p-5" data-testid="analytics-devices">
          <h3 className="text-white font-semibold text-sm mb-1 flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-emerald-300" strokeWidth={2.25} />
            Devices
          </h3>
          <p className="text-slate-500 text-[11px] mb-4">Traffic mix by device type</p>
          {['mobile', 'desktop', 'tablet', 'unknown'].map((d) => {
            const count = overview?.devices?.[d] || 0;
            const pct = deviceTotal ? Math.round((count / deviceTotal) * 100) : 0;
            if (count === 0 && d !== 'mobile' && d !== 'desktop') return null;
            return (
              <div key={d} className="mb-3 last:mb-0">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2 text-slate-300 text-[13px] capitalize">
                    <DeviceIcon device={d} />
                    {d}
                  </div>
                  <div className="text-[12px] tabular-nums text-slate-400">
                    <span className="text-white font-semibold">{count.toLocaleString()}</span>
                    <span className="text-slate-500 ml-2">{pct}%</span>
                  </div>
                </div>
                <div className="h-1.5 rounded-full bg-slate-800/70 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500"
                    style={{ width: `${Math.max(2, pct)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded-xl border border-slate-800/70 bg-slate-900/50 p-5" data-testid="analytics-countries">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="text-white font-semibold text-sm flex items-center gap-2">
              <Globe className="w-4 h-4 text-blue-300" strokeWidth={2.25} />
              Top countries
            </h3>
            <button
              onClick={handleBackfillGeo}
              disabled={backfilling}
              data-testid="analytics-backfill-geo-btn"
              className="flex items-center gap-1 px-2 h-6 rounded text-[10.5px] font-semibold text-blue-300 hover:text-white bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 hover:border-blue-500/50 transition disabled:opacity-50"
              title="Look up country for events that were captured before geo was wired up"
            >
              {backfilling ? (
                <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.5} />
              ) : (
                <Sparkles className="w-3 h-3" strokeWidth={2.5} />
              )}
              {backfilling ? 'Enriching…' : 'Backfill'}
            </button>
          </div>
          <p className="text-slate-500 text-[11px] mb-3">
            Resolved from client IP · cached per address
          </p>
          {backfillMsg && (
            <div className="mb-3 px-2.5 py-1.5 rounded-md bg-blue-500/10 border border-blue-500/30 text-[11px] text-blue-200">
              {backfillMsg}
            </div>
          )}
          {overview?.top_countries?.length ? (
            <div className="space-y-1.5">
              {overview.top_countries.map((c) => {
                const { flag, name } = countryDisplay(c.country);
                const max = Math.max(...overview.top_countries.map((x) => x.views));
                const pct = max ? Math.max(4, (c.views / max) * 100) : 0;
                return (
                  <div key={c.country || '_unknown'} className="group py-1">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-base leading-none" aria-hidden>{flag}</span>
                        <span className="text-[13px] text-slate-200 truncate">
                          {name}
                        </span>
                        <span className="text-[10px] text-slate-500 font-mono uppercase">
                          {c.country}
                        </span>
                      </div>
                      <span className="text-[12px] tabular-nums text-slate-400 font-semibold flex-shrink-0">
                        {c.views.toLocaleString()}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-800/70 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 group-hover:from-cyan-400 group-hover:to-blue-400 transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-slate-500 text-xs italic py-6 text-center leading-relaxed">
              No geo data yet.<br />
              Click <span className="text-blue-300 font-semibold">Backfill</span> above to resolve existing IPs.
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-800/70 bg-slate-900/50 p-5" data-testid="analytics-recent">
          <h3 className="text-white font-semibold text-sm mb-1 flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-300" strokeWidth={2.25} />
            Recent hits
          </h3>
          <p className="text-slate-500 text-[11px] mb-4">Live feed · last 5 minutes</p>
          <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
            {live?.recent?.length ? (
              live.recent.map((ev, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-slate-950/50 border border-slate-800/40 hover:border-slate-700 transition"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <DeviceIcon device={ev.device} />
                    <span className="text-[12px] font-mono text-slate-200 truncate">
                      {ev.path}
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-500 tabular-nums flex-shrink-0">
                    {relativeTime(ev.ts)}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-slate-500 text-xs italic py-4 text-center">
                Waiting for visitors…
              </div>
            )}
          </div>
        </div>
      </div>

      <p className="text-[11px] text-slate-500 leading-relaxed">
        First-party analytics — no cookies, no third-party trackers. Data is aggregated from
        pageviews on all public pages of the site. Admin routes (/admin/*) are excluded from
        every metric.
      </p>
    </AdminShell>
  );
};

export default AdminAnalyticsPage;
